import React, { useState, useEffect, useRef, createContext, useContext, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link, useParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Helmet } from "react-helmet-async";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { ArrowLeft, ArrowRight, Building2, ChevronLeft, ChevronRight, ClipboardList, Globe, LocateFixed, MapPin, MessageCircle, Phone, Search, Star, X } from "lucide-react";
import { getPhotoUrl, getCoverUrl, handlePhotoError } from "./components/PhotoManager";
import "@/App.css";
import DirectorySearch from "./pages/DirectorySearch";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import HotelCard, { SaveHotel, HotelCardSkeleton } from "./components/HotelCard";
import CityCombobox from "./components/CityCombobox";
import BudgetSelector from "./components/BudgetSelector";
import RoomTypePills from "./components/RoomTypePills";
import { api, getCities } from "./lib/api";
import { formatTZS, whatsappUrl, amenityMeta, readHotels, rememberHotel, trackCallClick, trackWhatsAppClick } from "./lib/hotels";
import { trackPageView, trackHotelView, sendBackendEvent } from "./analytics";

// Leaflet's default marker icon URLs break under webpack bundling unless
// re-pointed at the bundled asset URLs - this is the standard fix.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Context
const AuthContext = createContext(null);
const LanguageContext = createContext(null);

export const useAuth = () => useContext(AuthContext);
export const useLang = () => useContext(LanguageContext);

// Translations
const translations = {
  sw: {
    home: "Nyumbani",
    explore: "Tafuta",
    about: "Kuhusu",
    listHotel: "Sajili Hotel Yako",
    login: "Ingia",
    register: "Jisajili",
    logout: "Toka",
    searchHotels: "Tafuta Hoteli",
    city: "Mji",
    checkin: "Check-in",
    checkout: "Check-out",
    guests: "Wageni",
    search: "Tafuta",
    popularCities: "Tafuta kwa mji",
    featuredHotels: "Hoteli Bora",
    whyUs: "Kwa Nini Habari Stays?",
    mpesaPayment: "Lipa kwa M-Pesa",
    instantConfirm: "Uthibitishaji wa Papo Hapo",
    realPrices: "Bei Halisi za TZS",
    perNight: "kwa usiku",
    roomsAvailable: "Vyumba vinapatikana",
    viewMore: "Angalia Zaidi",
    bookNow: "Hifadhi Sasa",
    guestDetails: "Taarifa za Mgeni",
    fullName: "Jina Kamili",
    phone: "Simu",
    confirmPhone: "Thibitisha Simu",
    continue: "Endelea",
    bookingSummary: "Muhtasari wa Booking",
    nights: "Usiku",
    total: "Jumla",
    payWithMpesa: "Lipa na M-Pesa",
    payment: "Malipo",
    paymentInstructions: "Maelekezo ya M-Pesa",
    openMpesa: "Fungua M-Pesa",
    selectPayBill: "Chagua Lipa Bili",
    enterNumber: "Ingiza namba",
    amount: "Kiasi",
    enterPin: "PIN yako",
    completedPayment: "Nimemaliza Kulipa",
    cancelBooking: "Ghairi Booking",
    bookingConfirmed: "Booking Yako Imethibitishwa!",
    bookingExpired: "Booking imeisha muda",
    tryAgain: "Jaribu Tena",
    goHome: "Rudi Nyumbani",
    dashboard: "Dashibodi",
    bookings: "Bukini",
    myHotels: "Hoteli Zangu",
    roomTypes: "Aina za Vyumba",
    staff: "Wasimamizi",
    revenue: "Mapato",
    settings: "Mipangilio",
    reviews: "Maoni",
    amenities: "Huduma",
    photos: "Picha",
    description: "Maelezo",
    address: "Anwani",
    whatsapp: "WhatsApp",
    call: "Piga Simu",
    noRoomsAvailable: "Hakuna vyumba",
    filterByPrice: "Chuja kwa Bei",
    filterByAmenities: "Chuja kwa Huduma",
    sortBy: "Panga kwa",
    priceLowHigh: "Bei (Chini-Juu)",
    priceHighLow: "Bei (Juu-Chini)",
    rating: "Upimaji",
    hotelsFound: "hoteli zimepatikana",
    pendingVerification: "Akaunti yako inasubiri uthibitishaji",
    pendingMessage: "Utapigiwa simu ndani ya masaa 24",
    timeRemaining: "Muda uliobaki",
    password: "Neno la Siri",
    email: "Barua pepe",
    forgotPassword: "Umesahau neno la siri?",
    registerHotel: "Sajili hotel yako",
    orContinueWith: "au endelea na",
    google: "Google",
    tagline: "Karibu. Lala Vizuri.",
    // Dashboard shared
    save: "Hifadhi", cancel: "Ghairi", confirm: "Thibitisha", delete: "Futa",
    edit: "Hariri", add: "Ongeza", back: "Rudi", close: "Funga", status: "Hali",
    active: "Hai", inactive: "Imezimwa", name: "Jina", actions: "Vitendo",
    // Cashier dashboard
    todayCheckins: "Check-ins Leo", walkIn: "Walk-in Booking", activeGuests: "Wageni Waliopo",
    shiftSummary: "Muhtasari wa Shift", verifyBooking: "Thibitisha Booking",
    confirmArrival: "Thibitisha Kuwasili", confirmDeparture: "Thibitisha Kuondoka",
    awaiting: "Inasubiri", arrived: "Amewasili", departed: "Ameondoka",
    guestName: "Jina la Mgeni", guestPhone: "Simu ya Mgeni", roomType: "Aina ya Chumba",
    amountPaid: "Kiasi Kilicholipwa", selectRoom: "Chagua Chumba",
    guestInfo: "Taarifa za Mgeni", confirmRecord: "Thibitisha na Rekodi",
    paymentMethod: "Njia ya Malipo", cash: "Cash", card: "Kadi", notes: "Maelezo",
    printReceipt: "Chapisha Risiti", newWalkin: "Walk-in Nyingine",
    printSummary: "Chapisha Muhtasari", noCheckins: "Hakuna check-ins za leo kwa sasa.",
    noGuests: "Hakuna wageni waliopo sasa hivi.", noRooms: "Hakuna vyumba vinavyopatikana sasa hivi.",
    walkinRecorded: "Walk-in Imerekodishwa", checkinConfirmed: "Check-in Imethibitishwa",
    checkoutProcessed: "Checkout Imefanywa", totalRevenue: "Jumla ya Mapato Leo",
    walkinsToday: "Walk-ins Leo", onlineCheckins: "Check-ins Confirmed", checkouts: "Checkouts",
    cashCollected: "Cash", mpesaCollected: "M-Pesa", cardCollected: "Card",
    todayActivity: "Shughuli za Leo", noActivity: "Hakuna shughuli bado.",
    bookingNotFound: "Booking haikupatikana.", searchBooking: "Tafuta booking...",
    nightsLeft: "Usiku Zilizobaki", bookingType: "Aina ya Booking", ref: "Ref#",
    all: "Zote", unpaid: "Haijalipiwa", paid: "Imelipwa",
    // Admin dashboard
    allHotels: "Hoteli Zote", importHotels: "Ingiza Hoteli", cashiers: "Weka Hazina",
    owners: "Wamiliki", ownerApproval: "Thibitisha Wamiliki",
    addCashier: "Ongeza Mweka Hazina", resetPassword: "Weka Upya Neno la Siri",
    deactivate: "Zima", reactivate: "Washa", performance: "Utendaji",
    newPasswordLabel: "Neno la Siri Jipya", copyPassword: "Nakili",
    smsSent: "SMS imetumwa", hotelAssignment: "Hotel",
    // Owner dashboard
    myHotelsLabel: "Hoteli Zangu", editHotel: "Hariri Hotel",
    roomCount: "Aina za Vyumba", updateNeeded: "Sasisha",
  },
  en: {
    home: "Home",
    explore: "Search Hotels",
    about: "About",
    listHotel: "List Your Hotel",
    login: "Login",
    register: "Register",
    logout: "Logout",
    searchHotels: "Search Hotels",
    city: "City",
    checkin: "Check-in",
    checkout: "Check-out",
    guests: "Guests",
    search: "Search",
    popularCities: "Browse by city",
    featuredHotels: "Featured Hotels",
    whyUs: "Why Habari Stays?",
    mpesaPayment: "Pay with M-Pesa",
    instantConfirm: "Instant Confirmation",
    realPrices: "Real TZS Prices",
    perNight: "per night",
    roomsAvailable: "rooms available",
    viewMore: "View More",
    bookNow: "Book Now",
    guestDetails: "Guest Details",
    fullName: "Full Name",
    phone: "Phone",
    confirmPhone: "Confirm Phone",
    continue: "Continue",
    bookingSummary: "Booking Summary",
    nights: "Nights",
    total: "Total",
    payWithMpesa: "Pay with M-Pesa",
    payment: "Payment",
    paymentInstructions: "M-Pesa Instructions",
    openMpesa: "Open M-Pesa",
    selectPayBill: "Select Pay Bill",
    enterNumber: "Enter number",
    amount: "Amount",
    enterPin: "Your PIN",
    completedPayment: "I've Completed Payment",
    cancelBooking: "Cancel Booking",
    bookingConfirmed: "Your Booking is Confirmed!",
    bookingExpired: "Booking expired",
    tryAgain: "Try Again",
    goHome: "Go Home",
    dashboard: "Dashboard",
    bookings: "Bookings",
    myHotels: "My Hotels",
    roomTypes: "Room Types",
    staff: "Staff",
    revenue: "Revenue",
    settings: "Settings",
    reviews: "Reviews",
    amenities: "Amenities",
    photos: "Photos",
    description: "Description",
    address: "Address",
    whatsapp: "WhatsApp",
    call: "Call",
    noRoomsAvailable: "No rooms available",
    filterByPrice: "Filter by Price",
    filterByAmenities: "Filter by Amenities",
    sortBy: "Sort by",
    priceLowHigh: "Price (Low-High)",
    priceHighLow: "Price (High-Low)",
    rating: "Rating",
    hotelsFound: "hotels found",
    pendingVerification: "Your account is pending verification",
    pendingMessage: "You will receive a call within 24 hours",
    timeRemaining: "Time remaining",
    password: "Password",
    email: "Email",
    forgotPassword: "Forgot password?",
    registerHotel: "Register your hotel",
    orContinueWith: "or continue with",
    google: "Google",
    tagline: "Welcome. Sleep Well.",
    // Dashboard shared
    save: "Save", cancel: "Cancel", confirm: "Confirm", delete: "Delete",
    edit: "Edit", add: "Add", back: "Back", close: "Close", status: "Status",
    active: "Active", inactive: "Inactive", name: "Name", actions: "Actions",
    // Cashier dashboard
    todayCheckins: "Today's Check-ins", walkIn: "Walk-in Booking", activeGuests: "Active Guests",
    shiftSummary: "Shift Summary", verifyBooking: "Verify Booking",
    confirmArrival: "Confirm Arrival", confirmDeparture: "Confirm Departure",
    awaiting: "Awaiting", arrived: "Checked In", departed: "Checked Out",
    guestName: "Guest Name", guestPhone: "Guest Phone", roomType: "Room Type",
    amountPaid: "Amount Paid", selectRoom: "Select Room",
    guestInfo: "Guest Details", confirmRecord: "Confirm & Record",
    paymentMethod: "Payment Method", cash: "Cash", card: "Card", notes: "Notes",
    printReceipt: "Print Receipt", newWalkin: "New Walk-in",
    printSummary: "Print Summary", noCheckins: "No check-ins for today.",
    noGuests: "No active guests right now.", noRooms: "No rooms available right now.",
    walkinRecorded: "Walk-in Recorded", checkinConfirmed: "Check-in Confirmed",
    checkoutProcessed: "Checkout Processed", totalRevenue: "Total Revenue Today",
    walkinsToday: "Walk-ins Today", onlineCheckins: "Online Check-ins", checkouts: "Checkouts",
    cashCollected: "Cash", mpesaCollected: "M-Pesa", cardCollected: "Card",
    todayActivity: "Today's Activity", noActivity: "No activity yet.",
    bookingNotFound: "Booking not found.", searchBooking: "Search booking...",
    nightsLeft: "Nights Left", bookingType: "Booking Type", ref: "Ref#",
    all: "All", unpaid: "Unpaid", paid: "Paid",
    // Admin dashboard
    allHotels: "All Hotels", importHotels: "Import Hotels", cashiers: "Cashiers",
    owners: "Owners", ownerApproval: "Approve Owners",
    addCashier: "Add Cashier", resetPassword: "Reset Password",
    deactivate: "Deactivate", reactivate: "Reactivate", performance: "Performance",
    newPasswordLabel: "New Password", copyPassword: "Copy",
    smsSent: "SMS sent", hotelAssignment: "Hotel",
    // Owner dashboard
    myHotelsLabel: "My Hotels", editHotel: "Edit Hotel",
    roomCount: "Room Types", updateNeeded: "Update Needed",
  }
};

// Language Provider
const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(localStorage.getItem("lang") || "en");
  
  const t = useCallback((key) => translations[lang][key] || key, [lang]);
  
  const toggleLang = () => {
    const newLang = lang === "sw" ? "en" : "sw";
    setLang(newLang);
    localStorage.setItem("lang", newLang);
  };
  
  return (
    <LanguageContext.Provider value={{ lang, t, toggleLang }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Language Toggle Pill Component
const LanguageToggle = () => {
  const { lang, toggleLang } = useLang();
  return (
    <div className="flex bg-[#F4F4F5] rounded-full p-0.5 cursor-pointer" onClick={toggleLang} data-testid="language-toggle">
      <span className={`px-3 py-1 text-xs font-bold rounded-full transition-all ${lang === "sw" ? "bg-[#0F4C5C] text-white" : "text-[#52525B]"}`}>SW</span>
      <span className={`px-3 py-1 text-xs font-bold rounded-full transition-all ${lang === "en" ? "bg-[#0F4C5C] text-white" : "text-[#52525B]"}`}>EN</span>
    </div>
  );
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      setLoading(false);
      return;
    }

    // Try to get user from session
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
      localStorage.setItem("user", JSON.stringify(response.data));
    } catch {
      // Not authenticated
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await api.post("/auth/login", { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await api.post("/auth/register", data);
    const { access_token, user: userData } = response.data;
    if (access_token) {
      localStorage.setItem("token", access_token);
      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);
    }
    return userData;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  };

  const loginWithGoogle = async (accessToken) => {
    const response = await api.post("/auth/google", { access_token: accessToken });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const setUserData = (userData) => {
    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loginWithGoogle, loading, setUserData }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Loading Spinner
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-hs-white">
    <div className="animate-spin rounded-full h-10 w-10 border-4 border-hs-amber border-t-transparent" role="status" aria-label="Loading"></div>
  </div>
);

const SectionError = ({ message, onRetry }) => (
  <div role="alert" className="card flex flex-col items-center gap-3 p-8 text-center">
    <p className="text-sm text-hs-slate">{message}</p>
    <button type="button" onClick={onRetry} className="btn-outline">Try again</button>
  </div>
);

// ================== TRAVELER DASHBOARD ==================
const TravelerDashboard = ({ savedOnly = false }) => {
  const { user } = useAuth();
  const [saved, setSaved] = useState(() => readHotels("saved"));
  const [recent, setRecent] = useState(() => readHotels("recent"));
  useEffect(() => { setSaved(readHotels("saved")); setRecent(readHotels("recent")); }, [user]);
  const [nearby, setNearby] = useState(null);
  const [finding, setFinding] = useState(false);
  useEffect(() => { const update = () => setSaved(readHotels("saved")); window.addEventListener("hotel-preferences", update); return () => window.removeEventListener("hotel-preferences", update); }, []);
  const nearMe = () => {
    if (!navigator.geolocation) { toast.error("Location is unavailable. Search by city instead."); return; }
    setFinding(true);
    navigator.geolocation.getCurrentPosition(async ({ coords }) => {
      try {
        const { data } = await api.get(`/hotels?latitude=${coords.latitude}&longitude=${coords.longitude}`);
        setNearby(data);
      } catch { toast.error("Could not load nearby hotels"); } finally { setFinding(false); }
    }, () => { setFinding(false); toast.error("Location unavailable. Please search by city."); }, {timeout: 10000});
  };
  const firstName = user?.full_name?.split(/\s+/)[0];

  return (
    <div className="min-h-screen bg-hs-white">
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 py-8 md:py-12">
        <header className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-hs-charcoal">
              {savedOnly ? "Saved Hotels" : firstName ? `Welcome back, ${firstName}` : "My Account"}
            </h1>
            {!savedOnly && (
              <p className="mt-1 text-sm text-hs-muted">
                {user ? `${user.full_name} · ${user.email || user.phone || ""}` : <><Link to="/login" className="font-semibold text-hs-amber hover:text-hs-amber-dk">Sign in to your account</Link> to keep your hotels with you.</>}
              </p>
            )}
            <p className="mt-1 text-xs text-hs-muted">Saved and recently viewed hotels are stored on this device.</p>
          </div>
          {!savedOnly && (
            <div className="flex flex-wrap gap-3">
              <button type="button" disabled={finding} onClick={nearMe} className="btn-outline min-h-[44px] disabled:opacity-50">
                <LocateFixed className="h-4 w-4" />{finding ? "Finding hotels…" : "Hotels near me"}
              </button>
              <Link to="/hotels" className="btn-primary min-h-[44px] py-2.5">Search Hotels</Link>
            </div>
          )}
        </header>

        <section className="mb-12" aria-labelledby="saved-heading">
          {!savedOnly && <h2 id="saved-heading" className="mb-4 text-lg font-semibold text-hs-charcoal">Saved Hotels</h2>}
          {saved.length ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
              {saved.map(hotel => <HotelCard key={hotel.id} hotel={hotel} />)}
            </div>
          ) : (
            <div className="card flex flex-col items-center px-6 py-12 text-center">
              <span className="mb-3 text-4xl" aria-hidden="true">♡</span>
              <p className="mb-5 max-w-xs text-sm text-hs-muted">No saved hotels yet. Browse hotels and tap ♡ to save.</p>
              <Link to="/hotels" className="btn-primary">Browse Hotels</Link>
            </div>
          )}
        </section>

        {!savedOnly && (
          <section className="mb-12" aria-labelledby="recent-heading">
            <h2 id="recent-heading" className="mb-4 text-lg font-semibold text-hs-charcoal">Recently Viewed</h2>
            {recent.length ? (
              <div className="-mx-4 flex gap-4 overflow-x-auto px-4 pb-2 scrollbar-hide">
                {recent.map(hotel => <HotelCard key={hotel.id} hotel={hotel} layout="compact" />)}
              </div>
            ) : (
              <p className="text-sm text-hs-muted">Hotels you open will appear here. <Link to="/hotels" className="font-semibold text-hs-amber">Browse hotels</Link></p>
            )}
          </section>
        )}

        {!savedOnly && nearby && (
          <section aria-labelledby="nearby-heading">
            <h2 id="nearby-heading" className="mb-4 text-lg font-semibold text-hs-charcoal">Hotels near me</h2>
            {nearby.length ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                {nearby.map(hotel => <HotelCard key={hotel.id} hotel={hotel} />)}
              </div>
            ) : <p className="text-sm text-hs-muted">No hotels found near you yet. <Link to="/hotels" className="font-semibold text-hs-amber">Browse hotels</Link></p>}
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

// ================== LANDING PAGE ==================
const POPULAR_CITIES = ["Dodoma", "Mwanza", "Arusha", "Mbeya", "Tabora"];
const CITY_GRADIENTS = {
  Dodoma: "from-blue-800 to-blue-600",
  Mwanza: "from-teal-700 to-cyan-500",
  Arusha: "from-green-700 to-emerald-500",
  Mbeya: "from-purple-700 to-violet-500",
  Tabora: "from-orange-700 to-amber-500",
  Musoma: "from-rose-700 to-pink-500",
  Singida: "from-indigo-700 to-blue-500",
};

const buildSearchParams = ({ city, budget, roomType }) => {
  const params = new URLSearchParams();
  if (city) params.set("city", city);
  if (budget?.min) params.set("min_price", String(budget.min));
  if (budget?.max != null) params.set("max_price", String(budget.max));
  if (roomType) params.set("room_type", roomType);
  return params.toString();
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { lang } = useLang();
  const sw = lang === "sw";
  const [hotels, setHotels] = useState([]);
  const [hotelsStatus, setHotelsStatus] = useState("loading");
  const [cities, setCities] = useState([]);
  const [citiesStatus, setCitiesStatus] = useState("loading");
  const [searchData, setSearchData] = useState({ city: "", budget: null, roomType: "" });

  const loadHotels = useCallback(async () => {
    setHotelsStatus("loading");
    try {
      const res = await api.get("/hotels?sort=newest&limit=6");
      setHotels(res.data.slice(0, 6));
      setHotelsStatus("ready");
    } catch (err) {
      console.error(err);
      setHotelsStatus("error");
    }
  }, []);

  const loadCities = useCallback(async () => {
    setCitiesStatus("loading");
    try {
      setCities(await getCities());
      setCitiesStatus("ready");
    } catch (err) {
      console.error(err);
      setCitiesStatus("error");
    }
  }, []);

  useEffect(() => { loadHotels(); loadCities(); }, [loadHotels, loadCities]);

  const handleSearch = (event) => {
    event.preventDefault();
    navigate(`/hotels?${buildSearchParams(searchData)}`);
  };
  const quickSearch = city => navigate(`/hotels?${buildSearchParams({ ...searchData, city })}`);

  // Only cities with public hotel listings are shown -
  // hidden entirely rather than as dimmed "Coming soon" cards, given how sparse
  // coverage still is outside a handful of cities.
  const displayCities = cities.filter(c => c.hotel_count > 0);
  const totalHotels = displayCities.reduce((sum, c) => sum + c.hotel_count, 0);
  const stats = [
    [totalHotels >= 20 ? `${Math.floor(totalHotels / 10) * 10}+` : String(totalHotels || "190+"), sw ? "Hoteli zilizoorodheshwa" : "Hotels listed"],
    [String(displayCities.length || 6), sw ? "Miji" : "Cities covered"],
    [sw ? "Bure" : "Free", sw ? "kutumia" : "to use"],
  ];
  const steps = [
    [Search, sw ? "Tafuta" : "Search", sw ? "Chagua mji na bajeti yako. Pata hoteli zinazokufaa." : "Choose your city and budget. Find hotels that match your needs."],
    [ClipboardList, sw ? "Linganisha" : "Compare", sw ? "Angalia picha, bei na huduma. Soma taarifa halisi za kila hoteli." : "Browse photos, prices and amenities. Read real details about each hotel."],
    [Phone, sw ? "Wasiliana" : "Contact", sw ? "Piga simu au tuma WhatsApp kwa hoteli moja kwa moja. Hakuna ada, hakuna dalali." : "Call or WhatsApp the hotel directly. No booking fees, no middleman."],
  ];

  return (
    <div className="min-h-screen bg-hs-white">
      <Helmet>
      <title>Find Hotels in Tanzania | Habari Stays</title>
      <meta name="description" content="Find affordable hotels across Tanzania. Browse prices, photos and amenities for hotels in Dodoma, Mwanza, Arusha, Mbeya and more. Contact hotels directly on Habari Stays." />
      <link rel="canonical" href="https://habaristays.com/" />
      <meta property="og:title" content="Find Hotels in Tanzania | Habari Stays" />
      <meta property="og:description" content="Find affordable hotels across Tanzania. Browse prices, photos and amenities for hotels in Dodoma, Mwanza, Arusha, Mbeya and more. Contact hotels directly on Habari Stays." />
      <meta property="og:url" content="https://habaristays.com/" />
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section
        className="relative flex min-h-[380px] items-center overflow-hidden px-4 py-12 md:min-h-[520px] md:py-20"
        style={{ background: "linear-gradient(135deg, #1A1A2E 0%, #2D3561 50%, #E07B2A 100%)" }}
      >
        <img
          src="https://images.unsplash.com/photo-1596436889106-be35e843f974?w=1920"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
          onError={e => { e.currentTarget.style.display = "none"; }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />

        <div className="relative mx-auto w-full max-w-2xl">
          <div className="text-center">
            <p className="mb-3 text-sm font-medium text-white/70">🇹🇿 {sw ? "Saraka ya Hoteli Tanzania" : "Tanzania's Hotel Directory"}</p>
            <h1 className="mb-3 text-3xl font-bold leading-tight text-white md:text-5xl">
              {sw ? "Tafuta hoteli inayokufaa Tanzania" : "Find your perfect hotel in Tanzania"}
            </h1>
            <p className="mb-8 text-base text-white/80 md:text-lg">
              {sw ? "Tafuta hoteli zilizothibitishwa Tanzania. Linganisha bei na wasiliana moja kwa moja." : "Browse verified hotels across Tanzania. Compare prices and contact hotels directly."}
            </p>
          </div>

          <form onSubmit={handleSearch} className="space-y-4 rounded-2xl bg-white p-4 text-left shadow-2xl shadow-black/20 md:p-5">
            <div>
              <p className="section-label mb-2">{sw ? "Unaenda wapi?" : "Where are you going?"}</p>
              <CityCombobox value={searchData.city} onChange={city => setSearchData(prev => ({ ...prev, city }))} placeholder={sw ? "Chagua mji" : "Choose a city"} />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="min-w-0">
                <p className="section-label mb-2">{sw ? "Bajeti kwa usiku" : "Budget per night"}</p>
                <BudgetSelector value={searchData.budget} onChange={budget => setSearchData(prev => ({ ...prev, budget }))} />
              </div>
              <div className="min-w-0">
                <p className="section-label mb-2">{sw ? "Aina ya chumba" : "Room type"}</p>
                <RoomTypePills value={searchData.roomType} onChange={roomType => setSearchData(prev => ({ ...prev, roomType }))} />
              </div>
            </div>
            <button type="submit" className="btn-primary w-full py-4 text-base" data-testid="search-btn">
              <Search className="h-5 w-5" />{sw ? "Tafuta Hoteli" : "Search Hotels"}
            </button>
          </form>

          <div className="mt-4 flex items-center gap-2 overflow-x-auto scrollbar-hide md:justify-center">
            <span className="shrink-0 text-xs text-white/70">{sw ? "Maarufu:" : "Popular:"}</span>
            {POPULAR_CITIES.map(city => (
              <button key={city} type="button" onClick={() => quickSearch(city)} className="min-h-[36px] shrink-0 rounded-full bg-white/15 px-3.5 text-xs font-medium text-white backdrop-blur-sm transition-colors hover:bg-white hover:text-hs-charcoal">
                {city}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-hs-amber px-4 py-4" aria-label="Habari Stays in numbers">
        <dl className="mx-auto flex max-w-3xl justify-center gap-8 text-center sm:gap-16">
          {stats.map(([value, label], i) => (
            <div key={label} className="flex flex-col-reverse">
              <dt className="text-xs text-white/80">{label}</dt>
              <dd className={`text-xl font-bold text-white ${i < 2 && citiesStatus === "loading" ? "animate-pulse opacity-60" : ""}`}>{value}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="scroll-mt-16 bg-white px-4 py-16">
        <div className="mx-auto max-w-5xl">
          <h2 className="mb-2 text-center text-2xl font-bold text-hs-charcoal">{sw ? "Jinsi Habari Stays inavyofanya kazi" : "How Habari Stays works"}</h2>
          <p className="mb-12 text-center text-hs-muted">{sw ? "Pata na wasiliana na hoteli kwa hatua 3 rahisi" : "Find and contact hotels in 3 simple steps"}</p>
          <ol className="grid gap-4 md:grid-cols-3 md:gap-6">
            {steps.map(([Icon, title, body], i) => (
              <li key={title} className="rounded-2xl bg-hs-sage p-6 text-center">
                <p className="mb-2 text-xs font-bold text-hs-amber">{sw ? "HATUA" : "STEP"} {i + 1}</p>
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-hs-amber">
                  <Icon className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <h3 className="mb-2 font-semibold text-hs-charcoal">{title}</h3>
                <p className="text-sm leading-relaxed text-hs-muted">{body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Popular Cities */}
      <section id="cities" className="scroll-mt-16 bg-hs-white px-4 py-16">
        <div className="mx-auto max-w-7xl">
          <h2 className="mb-2 text-2xl font-bold text-hs-charcoal">{sw ? "Tafuta kwa mji" : "Browse by city"}</h2>
          <p className="mb-8 text-hs-muted">{sw ? "Hoteli zinapatikana kote Tanzania bara" : "Hotels available across mainland Tanzania"}</p>
          {citiesStatus === "error" ? (
            <SectionError message="We couldn't load cities right now." onRetry={loadCities} />
          ) : (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
              {citiesStatus === "loading"
                ? Array.from({ length: 4 }, (_, i) => <div key={i} className="aspect-[4/3] animate-pulse rounded-2xl bg-hs-sage" />)
                : displayCities.map(city => (
                  <Link
                    key={city.city}
                    to={`/hotels?city=${encodeURIComponent(city.city)}`}
                    className="group relative block aspect-[4/3] cursor-pointer overflow-hidden rounded-2xl"
                    data-testid={`city-${city.city}`}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${CITY_GRADIENTS[city.city] || "from-slate-700 to-slate-500"} transition-transform duration-200 group-hover:scale-[1.02]`} />
                    <div className="absolute inset-0 bg-black/20 transition-colors duration-200 group-hover:bg-black/10" />
                    <div className="absolute bottom-0 left-0 p-4">
                      <h3 className="text-lg font-bold text-white">{city.city}</h3>
                      <span className="mt-1 inline-flex rounded-full bg-white/20 px-2.5 py-1 text-xs text-white backdrop-blur-sm" data-testid={`city-hotel-count-${city.city}`}>
                        {city.hotel_count} {sw ? "hoteli" : city.hotel_count === 1 ? "hotel" : "hotels"}
                      </span>
                    </div>
                  </Link>
                ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Hotels */}
      <section className="bg-hs-sage px-4 py-16">
        <div className="mx-auto max-w-7xl">
          <h2 className="mb-8 text-2xl font-bold text-hs-charcoal">{sw ? "Hoteli zilizothibitishwa karibuni" : "Recently verified hotels"}</h2>
          {hotelsStatus === "error" ? (
            <SectionError message="We couldn't load hotels right now." onRetry={loadHotels} />
          ) : (
            <div className="-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-2 scrollbar-hide md:mx-0 md:grid md:grid-cols-3 md:gap-6 md:overflow-visible md:px-0 md:pb-0">
              {(hotelsStatus === "loading" ? Array.from({ length: 3 }, (_, i) => ({ skeleton: i })) : hotels).map(item => (
                <div key={item.skeleton ?? item.id} className="w-[85%] shrink-0 snap-start sm:w-[60%] md:w-auto">
                  {item.skeleton != null ? <HotelCardSkeleton /> : <HotelCard hotel={item} />}
                </div>
              ))}
            </div>
          )}
          <Link to="/hotels" className="mt-8 inline-flex items-center gap-1.5 font-semibold text-hs-amber hover:text-hs-amber-dk">
            {sw ? "Angalia hoteli zote" : "Browse all hotels"} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Owner CTA */}
      <section className="bg-white px-4 py-14">
        <div className="mx-auto flex max-w-5xl flex-col items-start gap-5 rounded-2xl border border-hs-border bg-hs-amber-lt p-6 md:flex-row md:items-center md:justify-between md:p-8">
          <div>
            <h2 className="text-xl font-bold text-hs-charcoal">{sw ? "Una hoteli? Jiunge nasi" : "Own a hotel? List it for free"}</h2>
            <p className="mt-1 text-sm text-hs-slate">{sw ? "Ongeza hotel yako kwenye Habari Stays na upate wateja zaidi" : "Add your hotel to Habari Stays and get more customers"}</p>
          </div>
          <Link to="/register-hotel" className="btn-primary shrink-0" data-testid="cta-register">{sw ? "Sajili Hotel Yako" : "List Your Hotel"}</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// ================== SEARCH PAGE ==================
const SearchPage = () => <DirectorySearch api={api} Navbar={Navbar} Footer={Footer} HotelCard={HotelCard} />;

// ================== HOTEL DETAIL PAGE ==================
const Stars = ({ value }) => (
  <span className="flex" aria-hidden="true">
    {[0, 1, 2, 3, 4].map(i => <Star key={i} className={`h-4 w-4 ${i < Math.round(value) ? "fill-hs-amber text-hs-amber" : "fill-hs-border text-hs-border"}`} />)}
  </span>
);

const PhotoGallery = ({ hotel, onOpen }) => {
  const photos = hotel.photos || [];
  const [index, setIndex] = useState(0);
  const touchStart = useRef(null);
  useEffect(() => { setIndex(0); }, [hotel.id]);

  if (!photos.length) {
    return (
      <div className="flex aspect-[16/9] w-full items-center justify-center rounded-2xl bg-gradient-to-br from-hs-sage to-hs-border">
        <Building2 className="h-12 w-12 text-hs-muted" aria-hidden="true" />
      </div>
    );
  }
  const go = step => setIndex(i => (i + step + photos.length) % photos.length);
  const arrow = "absolute top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-hs-charcoal shadow transition hover:bg-white md:flex";

  return (
    <div aria-label="Hotel photo gallery">
      <div
        className="relative overflow-hidden rounded-2xl bg-hs-sage"
        onTouchStart={e => { touchStart.current = e.touches[0].clientX; }}
        onTouchEnd={e => {
          if (touchStart.current == null) return;
          const dx = e.changedTouches[0].clientX - touchStart.current;
          touchStart.current = null;
          if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
        }}
      >
        <button type="button" className="block w-full" onClick={() => onOpen(getPhotoUrl(photos[index], "hd"))} aria-label="View photo full size">
          <img src={getPhotoUrl(photos[index], "web")} onError={handlePhotoError} alt={`${hotel.name} photo ${index + 1}`} className="aspect-[16/9] w-full object-cover" />
        </button>
        {photos.length > 1 && (
          <>
            <button type="button" onClick={() => go(-1)} className={`${arrow} left-3`} aria-label="Previous photo"><ChevronLeft className="h-5 w-5" /></button>
            <button type="button" onClick={() => go(1)} className={`${arrow} right-3`} aria-label="Next photo"><ChevronRight className="h-5 w-5" /></button>
            <span className="absolute bottom-3 right-3 rounded-full bg-black/60 px-2.5 py-1 text-xs font-medium text-white backdrop-blur-sm">{index + 1} / {photos.length}</span>
          </>
        )}
      </div>
      {photos.length > 1 && (
        <div className="mt-2 hidden gap-2 overflow-x-auto pb-1 scrollbar-hide sm:flex">
          {photos.map((photo, i) => (
            <button key={photo.id || i} type="button" onClick={() => setIndex(i)} aria-label={`Show photo ${i + 1}`} aria-current={i === index} className={`shrink-0 rounded-xl ${i === index ? "ring-2 ring-hs-amber ring-offset-2" : "opacity-80 hover:opacity-100"}`}>
              <img src={getPhotoUrl(photo, "thumb")} onError={handlePhotoError} alt="" loading="lazy" className="h-14 w-20 rounded-xl object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const websiteHref = url => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

const CallLink = ({ hotel, className, children }) => (
  <a className={className} href={`tel:${hotel.phone_number}`} onClick={() => trackCallClick(hotel)}>{children}</a>
);
const WhatsAppLink = ({ hotel, className, children }) => (
  <a className={className} href={whatsappUrl(hotel.whatsapp_number)} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsAppClick(hotel)}>{children}</a>
);
const whatsappButton = "flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-green-500 py-3 font-semibold text-white transition-colors hover:bg-green-600";

const ContactCard = ({ hotel, lang }) => (
  <div className="rounded-2xl border border-hs-border bg-white p-5 shadow-sm" aria-label="Contact hotel">
    {hotel.min_price != null ? (
      <div className="mb-4">
        <p className="text-xs text-hs-muted">{lang === "sw" ? "Kuanzia" : "From"}</p>
        <p className="text-2xl font-bold text-hs-charcoal">{formatTZS(hotel.min_price)}</p>
        <p className="text-xs text-hs-muted">/ night</p>
      </div>
    ) : <p className="mb-4 text-sm text-hs-muted">Contact the hotel for prices</p>}
    {hotel.phone_number && (
      <>
        <CallLink hotel={hotel} className="btn-primary w-full"><Phone className="h-4 w-4" />Call Hotel</CallLink>
        <p className="mb-3 mt-1.5 text-center text-xs text-hs-muted">{hotel.phone_number}</p>
      </>
    )}
    {hotel.whatsapp_number && <WhatsAppLink hotel={hotel} className={whatsappButton}><MessageCircle className="h-4 w-4" />WhatsApp</WhatsAppLink>}
    {hotel.website && (
      <a href={websiteHref(hotel.website)} target="_blank" rel="noopener noreferrer" className="btn-outline mt-2 w-full text-sm"><Globe className="h-4 w-4" />Visit Website</a>
    )}
    <SaveHotel hotel={hotel} className="mt-2 w-full text-sm" />
  </div>
);

const DetailSkeleton = () => (
  <div className="min-h-screen bg-hs-white">
    <Navbar showBottomNav={false} />
    <main className="mx-auto max-w-5xl animate-pulse px-4 py-8" aria-busy="true" aria-label="Loading hotel">
      <div className="mb-4 h-4 w-64 rounded bg-hs-sage" />
      <div className="aspect-[16/9] w-full rounded-2xl bg-hs-sage" />
      <div className="mt-6 h-7 w-2/3 rounded bg-hs-sage" />
      <div className="mt-3 h-4 w-1/3 rounded bg-hs-sage" />
      <div className="mt-10 space-y-3">{[0, 1, 2].map(i => <div key={i} className="h-4 rounded bg-hs-sage" />)}</div>
    </main>
  </div>
);

const SectionHeading = ({ children }) => <h2 className="mb-3 text-lg font-semibold text-hs-charcoal">{children}</h2>;

const HotelDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();
  const [hotel, setHotel] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [activePhoto, setActivePhoto] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportField, setReportField] = useState("price");
  const [reportNote, setReportNote] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    setLoadFailed(false);
    setHotel(null);
    fetchData();
  }, [id, attempt]);

  const fetchData = async () => {
    try {
      const [hotelRes, roomsRes, reviewsRes] = await Promise.all([
        api.get(`/hotels/${id}`),
        api.get(`/room-types?hotel_id=${id}`),
        api.get(`/reviews?hotel_id=${id}`)
      ]);
      setHotel(hotelRes.data);
      rememberHotel(hotelRes.data);
      trackHotelView(hotelRes.data.id, hotelRes.data.name, hotelRes.data.city);
      void sendBackendEvent("hotel_view", { hotel_id: hotelRes.data.id, city: hotelRes.data.city });
      setRooms(roomsRes.data);
      setReviews(reviewsRes.data);
    } catch (err) {
      // A 404 here just means the hotel isn't publicly visible right now
      // (missing data, or suspended) - that's shown via the dedicated
      // not-found state below, not an error toast.
      if (err.response?.status !== 404) {
        setLoadFailed(true);
        toast.error(lang === "sw" ? "Imeshindikana kupata hotel" : "Failed to load hotel");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <DetailSkeleton />;
  if (!hotel) return (
    <div className="min-h-screen bg-hs-white">
      <Navbar />
      <div className="flex min-h-[60vh] items-center justify-center px-4" data-testid="hotel-not-found">
        <div className="card max-w-md p-8 text-center">
          <span className="mb-4 block text-5xl" aria-hidden="true">🏨</span>
          {loadFailed ? (
            <>
              <p className="mb-2 text-xl font-semibold text-hs-charcoal">{lang === "sw" ? "Imeshindikana kupata hotel" : "We couldn't load this hotel."}</p>
              <p className="mb-6 text-sm text-hs-muted">{lang === "sw" ? "Angalia mtandao wako na ujaribu tena." : "Check your connection and try again."}</p>
              <button type="button" onClick={() => setAttempt(a => a + 1)} className="btn-primary">{lang === "sw" ? "Jaribu Tena" : "Try again"}</button>
            </>
          ) : (
            <>
              <p className="mb-2 text-xl font-semibold text-hs-charcoal">
                {lang === "sw" ? "Tangazo hili la hoteli halipatikani kwa sasa." : "This hotel listing is currently unavailable."}
              </p>
              <p className="mb-6 text-sm text-hs-muted">
                {lang === "sw"
                  ? "Huenda linakaguliwa au linakosa taarifa muhimu."
                  : "It may be under review or missing required information."}
              </p>
              <button type="button" onClick={() => navigate("/search")} className="btn-primary">
                {lang === "sw" ? "Tafuta Hoteli Nyingine" : "Find hotels in this city"} <ArrowRight className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );

  const isImported = hotel.status === "imported";
  const isVerified = hotel.status === "verified" || hotel.status === "owner_attached";

  const hasCoords = typeof hotel.latitude === "number" && typeof hotel.longitude === "number";
  const directionsUrl = hasCoords
    ? `https://www.google.com/maps?q=${hotel.latitude},${hotel.longitude}`
    : `https://www.google.com/maps/search/${encodeURIComponent(hotel.name)}+${encodeURIComponent(hotel.city)}+Tanzania`;

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    setReportSubmitting(true);
    try {
      await api.post(`/hotels/${hotel.id}/report`, {
        field_reported: reportField,
        note: reportNote || null,
      });
      toast.success(lang === "sw" ? "Asante! Ripoti yako imetumwa." : "Thanks! Your report was submitted.");
      setShowReportModal(false);
      setReportNote("");
      setReportField("price");
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error(lang === "sw" ? "Umetuma ripoti nyingi. Jaribu tena baadaye." : "Too many reports submitted. Please try again later.");
      } else {
        toast.error(lang === "sw" ? "Imeshindikana kutuma ripoti" : "Failed to submit report");
      }
    } finally {
      setReportSubmitting(false);
    }
  };

  const coverImage = getCoverUrl(hotel.photos, "web") || hotel.cover_photo || "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1920";
  const pageTitle = `${hotel.name} | Habari Stays`;
  const metaDescription = `${hotel.name} in ${hotel.city}, Tanzania. ${hotel.min_price != null ? `From ${formatTZS(hotel.min_price)}/night. ` : ""}View photos, amenities and contact details on Habari Stays.`;
  const canonicalUrl = `https://habaristays.com/hotel/${hotel.id}`;

  const hotelSchema = {
    "@context": "https://schema.org",
    "@type": "LodgingBusiness",
    "name": hotel.name,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": hotel.address,
      "addressLocality": hotel.city,
      "addressCountry": "TZ"
    },
    "telephone": hotel.phone_number,
    "image": coverImage,
  };

  const ratingValue = Number(hotel.average_rating || hotel.google_rating || 0);
  const reviewCount = hotel.review_count || hotel.google_review_count || 0;
  const hasContact = hotel.phone_number || hotel.whatsapp_number;

  return (
    <div className="min-h-screen bg-hs-white">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={coverImage} />
        <script type="application/ld+json">{JSON.stringify(hotelSchema)}</script>
      </Helmet>
      <Navbar showBottomNav={false} />

      <main className="mx-auto max-w-5xl px-4 py-6 md:py-8">
        <div className="mb-4 flex items-center gap-3">
          <button type="button" onClick={() => navigate(-1)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-hs-border bg-white text-hs-slate hover:border-hs-amber hover:text-hs-amber" aria-label={lang === "sw" ? "Rudi kwenye matokeo" : "Back to search results"} data-testid="back-to-search">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <nav aria-label="Breadcrumb" className="min-w-0 text-sm text-hs-muted">
            <ol className="flex items-center gap-1.5 overflow-hidden whitespace-nowrap">
              <li><Link to="/" className="hover:text-hs-amber">Home</Link></li>
              <li aria-hidden="true">›</li>
              <li><Link to="/hotels" className="hover:text-hs-amber">Hotels</Link></li>
              <li aria-hidden="true">›</li>
              <li><Link to={`/hotels?city=${encodeURIComponent(hotel.city)}`} className="hover:text-hs-amber">{hotel.city}</Link></li>
              <li aria-hidden="true" className="hidden sm:block">›</li>
              <li aria-current="page" className="hidden truncate text-hs-charcoal sm:block">{hotel.name}</li>
            </ol>
          </nav>
        </div>

        <PhotoGallery hotel={hotel} onOpen={setActivePhoto} />

        {/* Unverified Warning Banner */}
        {!isVerified && !isImported && (
          <div className="mt-4 flex items-start gap-2 rounded-xl border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
            <span aria-hidden="true">⚠️</span>
            {lang === "sw" ? "Hoteli hii bado haijathibitishwa na Habari Stays. Unaweza kuwasiliana nao moja kwa moja." : "This hotel is not yet verified by Habari Stays. You can contact them directly."}
          </div>
        )}

        <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1fr)_16rem]">
          <div className="min-w-0 space-y-10">
            {/* Header */}
            <header>
              <div className="mb-2 flex flex-wrap gap-2">
                {isVerified && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-700" data-testid="hotel-verified-badge" title={lang === "sw" ? "Hoteli hii imethibitishwa na Habari Stays" : "This hotel is verified by Habari Stays"}>
                    ✓ {lang === "sw" ? "Imethibitishwa" : "Verified"}
                  </span>
                )}
                {isImported && (
                  <span className="inline-flex rounded-full bg-hs-amber-lt px-2.5 py-1 text-xs font-semibold text-hs-amber-dk">
                    {lang === "sw" ? "Wasiliana Moja kwa Moja" : "Contact Directly"}
                  </span>
                )}
              </div>
              <div className="flex items-start justify-between gap-3">
                <h1 className="text-2xl font-bold text-hs-charcoal">{hotel.name}</h1>
                <SaveHotel hotel={hotel} variant="icon" className="shrink-0 border border-hs-border lg:hidden" />
              </div>
              <p className="mt-1 flex items-start gap-1 text-sm text-hs-muted">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                {hotel.city}{hotel.address ? ` · ${hotel.address}` : ""}
              </p>
              {ratingValue > 0 && (
                <p className="mt-1 flex items-center gap-2 text-sm text-hs-muted">
                  <Stars value={ratingValue} />
                  <span>{ratingValue.toFixed(1)} ({reviewCount} {reviewCount === 1 ? "review" : "reviews"})</span>
                </p>
              )}
              {hotel.min_price != null && (
                <p className="mt-4 text-hs-charcoal lg:hidden">
                  <span className="text-sm text-hs-muted">{lang === "sw" ? "Kuanzia " : "From "}</span>
                  <span className="text-xl font-bold">{formatTZS(hotel.min_price)}</span>
                  <span className="text-sm text-hs-muted"> / night</span>
                </p>
              )}
            </header>

            {hotel.description && (
              <section>
                <SectionHeading>{lang === "sw" ? "Kuhusu" : "About"}</SectionHeading>
                <p className="whitespace-pre-line text-sm leading-relaxed text-hs-slate">{hotel.description}</p>
              </section>
            )}

            {hotel.amenities?.length > 0 && (
              <section>
                <SectionHeading>{lang === "sw" ? "Huduma" : "Amenities"}</SectionHeading>
                <ul className="grid grid-cols-2 gap-3 md:grid-cols-3">
                  {hotel.amenities.map(name => {
                    const { icon, label } = amenityMeta(name);
                    return (
                      <li key={name} className="flex items-center gap-3 rounded-xl bg-hs-sage p-3">
                        <span className="text-2xl leading-none" aria-hidden="true">{icon}</span>
                        <span className="text-sm font-medium text-hs-charcoal">{label}</span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            <section data-testid="room-types">
              <SectionHeading>{lang === "sw" ? "Aina za Vyumba na Bei" : "Room Types & Prices"}</SectionHeading>
              {rooms.length ? (
                <ul className="space-y-3">
                  {rooms.map(room => (
                    <li key={room.id} className="flex items-center justify-between gap-4 rounded-xl border border-hs-border bg-white p-4">
                      <div className="min-w-0">
                        <p className="font-medium text-hs-charcoal">{room.name}</p>
                        <p className="text-xs text-hs-muted">
                          {room.capacity != null && `${room.capacity} ${room.capacity === 1 ? "person" : "persons"}`}
                          {room.capacity != null && room.description && " · "}
                          {room.description}
                        </p>
                      </div>
                      <p className="shrink-0 text-right">
                        <span className="block font-bold text-hs-amber">{formatTZS(room.price_per_night)}</span>
                        <span className="text-xs text-hs-muted">/ night</span>
                      </p>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-sm text-hs-muted">Contact the hotel for room prices.</p>}
            </section>

            <section>
              <SectionHeading>{lang === "sw" ? "Mahali" : "Location"}</SectionHeading>
              {hasCoords && (
                <div className="isolate mb-3 h-64 w-full overflow-hidden rounded-2xl border border-hs-border" data-testid="hotel-map">
                  <MapContainer
                    center={[hotel.latitude, hotel.longitude]}
                    zoom={15}
                    style={{ height: "100%", width: "100%" }}
                    scrollWheelZoom={false}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={[hotel.latitude, hotel.longitude]}>
                      <Popup>{hotel.name}</Popup>
                    </Marker>
                  </MapContainer>
                </div>
              )}
              <p className="text-sm text-hs-slate">{[hotel.address, hotel.city, "Tanzania"].filter(Boolean).join(", ")}</p>
              <a
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex min-h-[44px] items-center gap-1.5 text-sm font-semibold text-hs-amber hover:text-hs-amber-dk"
                data-testid="get-directions-btn"
              >
                {lang === "sw" ? "Pata Maelekezo" : "Get Directions"} <ArrowRight className="h-4 w-4" />
              </a>
            </section>

            {/* Reviews */}
            {reviews.length > 0 && (
              <section>
                <SectionHeading>{lang === "sw" ? "Maoni" : "Reviews"} ({reviews.length})</SectionHeading>
                <ul className="space-y-3">
                  {reviews.map((review) => (
                    <li key={review.id} className="rounded-xl border border-hs-border bg-white p-4">
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <span className="font-medium text-hs-charcoal">{review.guest_name}</span>
                        <Stars value={review.rating} />
                      </div>
                      <p className="text-sm text-hs-slate">{review.comment}</p>
                      <p className="mt-2 text-xs text-hs-muted">{new Date(review.created_at).toLocaleDateString()}</p>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <button
              type="button"
              onClick={() => setShowReportModal(true)}
              className="min-h-[44px] text-xs text-hs-muted hover:text-hs-slate hover:underline"
              data-testid="report-incorrect-info-link"
            >
              {lang === "sw" ? "Taarifu makosa kwenye taarifa hii" : "Report incorrect info"}
            </button>
          </div>

          <aside className="hidden lg:block">
            <div className="sticky top-24"><ContactCard hotel={hotel} lang={lang} /></div>
          </aside>
        </div>
      </main>

      {/* Mobile contact bar */}
      {hasContact && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-hs-border bg-white px-4 pt-3 pb-safe lg:hidden">
          <div className="mx-auto flex max-w-5xl gap-3">
            {hotel.phone_number && <CallLink hotel={hotel} className="btn-primary min-h-[48px] flex-1"><Phone className="h-4 w-4" />Call</CallLink>}
            {hotel.whatsapp_number && <WhatsAppLink hotel={hotel} className={`${whatsappButton} flex-1`}><MessageCircle className="h-4 w-4" />WhatsApp</WhatsAppLink>}
          </div>
        </div>
      )}

      <Footer />

      {/* Report Incorrect Info Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" onClick={() => setShowReportModal(false)}>
          <div className="w-full max-w-sm space-y-4 rounded-t-2xl bg-white p-6 pb-safe sm:rounded-2xl sm:pb-6" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="report-title" data-testid="report-issue-modal">
            <div className="flex items-center justify-between">
              <h3 id="report-title" className="text-lg font-bold text-hs-charcoal">
                {lang === "sw" ? "Taarifu Tatizo" : "Report Incorrect Info"}
              </h3>
              <button type="button" onClick={() => setShowReportModal(false)} className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-hs-sage" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleReportSubmit} className="space-y-4">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-hs-charcoal">{lang === "sw" ? "Kuna tatizo gani?" : "What's wrong?"}</span>
                <select
                  value={reportField}
                  onChange={(e) => setReportField(e.target.value)}
                  className="input-base"
                  data-testid="report-field-select"
                >
                  <option value="price">{lang === "sw" ? "Bei" : "Price"}</option>
                  <option value="phone">{lang === "sw" ? "Namba ya Simu" : "Phone number"}</option>
                  <option value="address">{lang === "sw" ? "Anwani" : "Address"}</option>
                  <option value="closed">{lang === "sw" ? "Hoteli Imefungwa" : "Hotel is closed"}</option>
                  <option value="other">{lang === "sw" ? "Nyingine" : "Other"}</option>
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-hs-charcoal">{lang === "sw" ? "Maelezo (hiari)" : "Details (optional)"}</span>
                <textarea
                  value={reportNote}
                  onChange={(e) => setReportNote(e.target.value)}
                  rows={3}
                  maxLength={500}
                  className="input-base"
                  data-testid="report-note-input"
                />
              </label>
              <button
                type="submit"
                disabled={reportSubmitting}
                className="btn-primary w-full"
                data-testid="report-submit-btn"
              >
                {reportSubmitting ? "..." : (lang === "sw" ? "Tuma" : "Submit")}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Photo Lightbox */}
      {activePhoto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" onClick={() => setActivePhoto(null)} data-testid="photo-lightbox">
          <div className="relative w-full max-w-5xl" onClick={e => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setActivePhoto(null)}
              className="absolute -right-3 -top-3 flex h-11 w-11 items-center justify-center rounded-full bg-white text-hs-charcoal shadow-lg hover:bg-hs-sage"
              aria-label="Close photo viewer"
            >
              <X className="h-5 w-5" />
            </button>
            <img onError={handlePhotoError} src={activePhoto} alt="Hotel detail" className="w-full rounded-2xl shadow-2xl" />
          </div>
        </div>
      )}
    </div>
  );
};

// Lazy load dashboard components
const OwnerDashboard = React.lazy(() => import("./pages/OwnerDashboard"));
const CashierDashboard = React.lazy(() => import("./pages/CashierDashboard"));
const AdminDashboard = React.lazy(() => import("./pages/AdminDashboard"));
const BackofficeDashboard = React.lazy(() => import("./pages/BackofficeDashboard"));
const LoginPage = React.lazy(() => import("./pages/AuthPages").then(m => ({ default: m.LoginPage })));
const RegisterPage = React.lazy(() => import("./pages/AuthPages").then(m => ({ default: m.RegisterPage })));
const RegisterHotelPage = React.lazy(() => import("./pages/AuthPages").then(m => ({ default: m.RegisterHotelPage })));

// ================== APP ROUTER ==================
function AppRouter() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);

  // Scroll to in-page sections (e.g. "/#how-it-works"); retry briefly because
  // the target section may render after its data loads.
  useEffect(() => {
    if (!hash) return undefined;
    let tries = 0;
    let timer;
    const scroll = () => {
      const target = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (target) target.scrollIntoView?.({ behavior: "smooth", block: "start" });
      else if (tries++ < 10) timer = setTimeout(scroll, 100);
    };
    timer = setTimeout(scroll, 0);
    return () => clearTimeout(timer);
  }, [pathname, hash]);

  if (pathname.split("/").includes("book")) return <Navigate to="/hotels" replace />;
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/hotels" element={<SearchPage />} />
      <Route path="/saved" element={<TravelerDashboard savedOnly />} />
      <Route path="/account" element={<TravelerDashboard />} />
      <Route path="/dashboard" element={<TravelerDashboard />} />
      <Route path="/search" element={<SearchPage />} />
      <Route path="/hotel/:id" element={<HotelDetailPage />} />
      <Route path="/hotels/:city/:slug" element={<HotelDetailPage />} />
      <Route path="/login" element={<React.Suspense fallback={<LoadingSpinner />}><LoginPage /></React.Suspense>} />
      <Route path="/register" element={<React.Suspense fallback={<LoadingSpinner />}><RegisterPage /></React.Suspense>} />
      <Route path="/register-hotel" element={<React.Suspense fallback={<LoadingSpinner />}><RegisterHotelPage /></React.Suspense>} />
      <Route path="/booking/*" element={<Navigate to="/hotels" replace />} />
      <Route path="/checkout/*" element={<Navigate to="/hotels" replace />} />
      
      {/* Owner Routes */}
      <Route
        path="/owner/*"
        element={
          <ProtectedRoute roles={["owner"]}>
            <React.Suspense fallback={<LoadingSpinner />}>
              <OwnerDashboard />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Cashier Routes */}
      <Route
        path="/cashier/*"
        element={
          <ProtectedRoute roles={["cashier"]}>
            <React.Suspense fallback={<LoadingSpinner />}>
              <CashierDashboard />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
      
      {/* Admin Routes */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute roles={["admin"]}>
            <React.Suspense fallback={<LoadingSpinner />}>
              <AdminDashboard />
            </React.Suspense>
          </ProtectedRoute>
        }
      />

      {/* Backoffice Routes */}
      <Route
        path="/backoffice/*"
        element={
          <ProtectedRoute roles={["backoffice"]}>
            <React.Suspense fallback={<LoadingSpinner />}>
              <BackofficeDashboard />
            </React.Suspense>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

// ================== APP ==================
function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ""}>
      <LanguageProvider>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
export { api, LoadingSpinner, formatTZS, LanguageToggle };
